import * as React from 'react';
import cn from 'clsx';
import { ArticleHeading1 } from '@cian/ui-kit';
import { ConditionalWrapper } from '@cian/conditional-component';

import { withDeviceType, IWithDeviceTypeProps } from '../../utils/withDeviceType';
import { BigHeading } from '../BigHeading';
import { IAdditionalService } from '../../types/promoInfo';
import { AdditionalServiceCard } from '../AdditionalServiceCard';
import { SberPremierDetailsModal } from '../AdditionalServiceCard/components/SberPremierDetailsModal';
import { SberPremierDetailsButton } from '../AdditionalServiceCard/components/SberPremierDetailsButton';

import * as styles from './styles.css';

interface IAdditionalServicesSectionProps extends IWithDeviceTypeProps {
  additionalServices: IAdditionalService[];
  onAdditionalServiceClick: (service: IAdditionalService) => void;
}

const getExtraActions = ({ extraServices }: IAdditionalService): React.ReactNode => {
  if (!extraServices?.sberPremier) {
    return null;
  }

  const {
    sberPremier: { presentation, opener },
  } = extraServices;

  return (
    <SberPremierDetailsModal
      presentation={presentation}
      activator={activate => <SberPremierDetailsButton onClick={activate} text={opener.text} icon={opener.icon} />}
    />
  );
};

/**
 * Секция "Способы взаиморасчётов".
 */
const AdditionalServicesSection: React.FC<React.PropsWithChildren<IAdditionalServicesSectionProps>> = ({
  additionalServices,
  onAdditionalServiceClick,
  isPhone,
  deviceType,
}) => {
  const gridTracksCount = React.useMemo(() => {
    const maxTracks = deviceType === 'desktop' ? 3 : deviceType === 'tablet' ? 2 : 1;

    return Math.min(additionalServices.length - 1, maxTracks);
  }, [additionalServices.length, deviceType]);

  return (
    <>
      <ConditionalWrapper if={isPhone} ThenComponent={ArticleHeading1} ElseComponent={BigHeading as React.FC} as="h2">
        Способы взаиморасчётов
      </ConditionalWrapper>
      <div
        className={cn(styles['services'], styles[deviceType])}
        style={
          {
            '--services-grid-tracks-count': gridTracksCount,
            '--services-tail': (additionalServices.length - 1) % gridTracksCount,
          } as React.CSSProperties
        }
        data-name="AdditionalServicesSection"
      >
        {additionalServices.map((service, i) => (
          <AdditionalServiceCard
            key={i}
            data={service}
            extraActions={getExtraActions(service)}
            onAboutClick={() => onAdditionalServiceClick(service)}
            isExtendedCard={false}
          />
        ))}
      </div>
    </>
  );
};

const AdditionalServicesSectionWithDeviceType = withDeviceType(AdditionalServicesSection);
export { AdditionalServicesSectionWithDeviceType as AdditionalServicesSection };
